// The funnel. This file IS the walk: the questions, their words, their options
// and the order they come in, plus the one fork in it.
//
// It used to be config/onboarding_flow.yml, served to the client through
// GraphQL. That earned its keep while every screen was rendered by a generic
// type -> component dispatch, because a new question really was a config edit.
// It stopped being true: every screen on the walk is now its own component,
// designed for its own question, and a YAML that only supplied strings to
// hand-written React was a second place to look for one answer.
//
// The server keeps no copy of any of this. It stores what it is given under the
// key it is given and scores the bag afterwards (Onboarding::Scoring), so adding
// a question is this file and a component — no migration, no schema change, no
// codegen.
//
// The one thing that must not change casually is a `key`. Answers are stored
// under it and a lead mid-funnel resumes by it, so renaming a live one orphans
// answers and sends people back a step.

export type Field = {
  key: string;
  label: string;
  placeholder?: string;
  inputType?: "text" | "email";
};

export type Option = { id: string; name: string; subtitle?: string };

export type Step = {
  key: string;
  chapter: string;
  title: string;
  subtitle?: string;
  // False renders a skip affordance. Default true.
  required?: boolean;
  // Whether more than one answer can be picked. A single pick IS the answer and
  // advances on the click; a multi has nothing to advance on, so it grows a
  // Continue once there is something to continue with.
  multi?: boolean;
  fields?: Field[];
  options?: Option[];
  // One question with several sets of answers, chosen by what came before.
  optionGroups?: Record<string, Option[]>;
};

export const STEP = {
  NAME: "name",
  VISITOR_TYPE: "visitor_type",
  ROLE_OTHER: "role_other",
  COMPANY_TYPE: "company_type",
  CLIENT_TYPES: "client_types",
  ROLE: "role",
  FOCUS: "focus",
  STACK: "software_stack",
  EMAIL: "email",
  DATA_HUB: "data_hub",
  WAITLIST: "waitlist",
} as const;

const ADVISOR = "ADVISOR";

// The five sets of cards the focus question can show, plus the fallback.
const FOCUS_GROUPS = ["SAAS", "AGENCY", "ECOMMERCE", "LOCAL"];
const GENERAL = "GENERAL";

export const STEPS: Step[] = [
  // The funnel opens with Dominique saying hello and asking one thing. Every
  // question after this one can use `{name}` in its copy — personalise()
  // substitutes what was typed here and drops the whole clause when there is
  // nothing to put in it, which is why every use of it is a trailing ", {name}".
  {
    key: STEP.NAME,
    chapter: "About you",
    title: "Hi there! I'm Dominique, nice to meet you",
    subtitle:
      "I think finance for start-ups and local businesses needs a revolution, so I built software that is both simple and super insightful. Sounds interesting? Let's get started!",
    fields: [
      {
        key: "first_name",
        label: "First name",
        placeholder: "Your first name",
      },
      { key: "last_name", label: "Last name", placeholder: "Your last name" },
    ],
  },

  // Who's asking, and the funnel's one fork: what someone says they are decides
  // whether they're asked their role in their own words, and which of the two
  // company questions they get.
  {
    key: STEP.VISITOR_TYPE,
    chapter: "About you",
    title: "What best describes you, {name}?",
    // "Neither" is an empty answer rather than a third card, which is what makes
    // it a skip and sends them to the role question.
    required: false,
    options: [
      { id: "BUSINESS", name: "Startup or small business" },
      { id: ADVISOR, name: "Investor/consultant" },
    ],
  },

  {
    key: STEP.ROLE_OTHER,
    chapter: "About you",
    title: "What's your role, {name}?",
    fields: [{ key: "role", label: "Role", placeholder: "Your role" }],
  },

  // Same five options twice, in the singular for someone describing their own
  // company and in the plural for someone describing their clients. Two steps
  // rather than one with conditional copy: a question's words are its own.
  {
    key: STEP.COMPANY_TYPE,
    chapter: "About you",
    title: "What company do you work for?",
    options: [
      { id: "SAAS", name: "SaaS / Start-up" },
      { id: "AGENCY", name: "Agency" },
      { id: "ECOMMERCE", name: "E-commerce" },
      { id: "LOCAL", name: "Local business" },
      { id: "OTHER", name: "Other" },
    ],
  },

  {
    key: STEP.CLIENT_TYPES,
    chapter: "About you",
    title: "What kind of companies do you work with?",
    multi: true,
    options: [
      { id: "SAAS", name: "SaaS / Start-ups" },
      { id: "AGENCY", name: "Agencies" },
      { id: "ECOMMERCE", name: "E-commerce" },
      { id: "LOCAL", name: "Local businesses" },
      { id: "OTHER", name: "Other" },
    ],
  },

  // Asked only of someone describing their own company, and only when they have
  // not already written the answer in the box above. It is the single strongest
  // input to the fit score: a founder can say yes to us on the call, and someone
  // keeping another company's books has to go and ask.
  {
    key: STEP.ROLE,
    chapter: "About you",
    title: "And what's your role there, {name}?",
    options: [
      { id: "FOUNDER", name: "Founder or CEO" },
      { id: "FINANCE_LEAD", name: "Finance lead or CFO" },
      { id: "OPERATIONS", name: "Operations" },
      { id: "ACCOUNTANT", name: "Accountant or bookkeeper" },
      { id: "OTHER", name: "Something else" },
    ],
  },

  // One question, five sets of answers. Which set renders is decided by the
  // company question above — a SaaS founder and a shop owner are not chasing the
  // same number, and this is the screen where the funnel says so out loud.
  // GENERAL is the fallback: it covers "Other", and an advisor who named several
  // kinds.
  {
    key: STEP.FOCUS,
    chapter: "About you",
    title: "What number is top of mind for you right now, {name}?",
    optionGroups: {
      SAAS: [
        {
          id: "KPIS",
          name: "Core KPIs",
          subtitle:
            "MRR, growth rate and churn, broken down by revenue and by tenure — every number you need to run a SaaS.",
        },
        {
          id: "RUNWAY",
          name: "Runway",
          subtitle:
            "Your runway is your lifeline. See what it's built from, and watch how it moves month to month.",
        },
        {
          id: "PAYBACK",
          name: "CAC payback",
          subtitle:
            "What a customer costs to win, and how many months until they've paid you back.",
        },
        {
          id: "COHORTS",
          name: "Cohort retention",
          subtitle:
            "Follow every signup month forward and see which cohorts actually stay.",
        },
        {
          id: "SPEND",
          name: "Where the money goes",
          subtitle:
            "Every dollar out, sorted by who you paid and what for, with nothing left in “uncategorised”.",
        },
        {
          id: "RULE_OF_40",
          name: "Rule of 40",
          subtitle:
            "Growth plus margin, quarter by quarter — the number an investor opens with.",
        },
      ],
      AGENCY: [
        {
          id: "CLIENT_MARGIN",
          name: "Client profitability",
          subtitle:
            "Which clients make money once payroll, contractors and pass-throughs are counted against them.",
        },
        {
          id: "CASH",
          name: "Cash position",
          subtitle:
            "What's actually in the bank across every account, on any day you pick.",
        },
        {
          id: "RUNWAY",
          name: "Runway",
          subtitle:
            "How long today's costs run on today's cash, and what would change that.",
        },
        {
          id: "CONCENTRATION",
          name: "Revenue concentration",
          subtitle:
            "How much of your income rests on your largest few clients — and what that risks.",
        },
        {
          id: "SPEND",
          name: "Where the money goes",
          subtitle:
            "Payroll, contractors, software and everything else, sorted by who you paid.",
        },
        {
          id: "MARGIN",
          name: "Margin by month",
          subtitle:
            "What you kept after costs, month by month, without waiting for anyone to close the books.",
        },
      ],
      ECOMMERCE: [
        {
          id: "TRUE_MARGIN",
          name: "True margin",
          subtitle:
            "What's left after processing fees, refunds and cost of goods — not the number on the dashboard.",
        },
        {
          id: "FEES",
          name: "What payments cost",
          subtitle:
            "What Stripe and the rest actually take, as a share of every sale, tracked over time.",
        },
        {
          id: "CASH",
          name: "Cash position",
          subtitle:
            "What's in the bank across processors and accounts, on any day you pick.",
        },
        {
          id: "RUNWAY",
          name: "Runway",
          subtitle:
            "How long today's costs run on today's cash, with inventory counted honestly.",
        },
        {
          id: "REPEAT",
          name: "Repeat revenue",
          subtitle:
            "How much comes from customers who came back, cohort by cohort.",
        },
        {
          id: "SPEND",
          name: "Where the money goes",
          subtitle:
            "Ads, inventory, shipping, software — every dollar out, sorted by who you paid.",
        },
      ],
      LOCAL: [
        {
          id: "CASH",
          name: "Cash position",
          subtitle:
            "What's actually in the bank across every account, on any day you pick.",
        },
        {
          id: "RUNWAY",
          name: "Runway",
          subtitle:
            "How long today's costs run on today's cash, and what would change that.",
        },
        {
          id: "SPEND",
          name: "Where the money goes",
          subtitle:
            "Every dollar out, sorted by who you paid and what for, with nothing left unlabelled.",
        },
        {
          id: "REVENUE",
          name: "Revenue by month",
          subtitle:
            "What actually came in, month by month, without waiting for the bookkeeper.",
        },
        {
          id: "PAYROLL",
          name: "What your team costs",
          subtitle:
            "Wages, contractors and the rest, set against what the work brought in.",
        },
        {
          id: "PROFIT",
          name: "Profit, honestly",
          subtitle:
            "What you kept after everything, on the same basis every month.",
        },
      ],
      GENERAL: [
        {
          id: "CASH",
          name: "Cash position",
          subtitle:
            "What's actually in the bank across every account, on any day you pick.",
        },
        {
          id: "RUNWAY",
          name: "Runway",
          subtitle:
            "How long today's costs run on today's cash, and what would change that.",
        },
        {
          id: "REVENUE",
          name: "Revenue and growth",
          subtitle: "What came in, month by month, and how fast it's moving.",
        },
        {
          id: "SPEND",
          name: "Where the money goes",
          subtitle: "Every dollar out, sorted by who you paid and what for.",
        },
        {
          id: "UNIT_ECONOMICS",
          name: "Customer economics",
          subtitle:
            "What a customer costs to win, and what they're worth once they're in.",
        },
        {
          id: "PROFIT",
          name: "Profit, honestly",
          subtitle:
            "What was kept after everything, on the same basis every month.",
        },
      ],
    },
  },

  // Twenty names, chosen to cover most of a SaaS, agency, e-commerce or local
  // business finance stack. The order is books, money in, banks and cards,
  // people, bills, and the honest answer at the end. What each of them will let
  // us read is sources.ts; how much a given stack is worth to us is the `stack`
  // dimension of the fit score.
  {
    key: STEP.STACK,
    chapter: "About you",
    title:
      "What accounting/finance software and banks/fintech are part of your stack?",
    subtitle: "Pick everything you use.",
    required: false,
    options: [
      { id: "QUICKBOOKS", name: "QuickBooks" },
      { id: "XERO", name: "Xero" },
      { id: "SAGE", name: "Sage" },
      { id: "STRIPE", name: "Stripe" },
      { id: "SHOPIFY", name: "Shopify" },
      { id: "PAYPAL", name: "PayPal" },
      { id: "SQUARE", name: "Square" },
      { id: "PADDLE", name: "Paddle" },
      { id: "CHARGEBEE", name: "Chargebee" },
      { id: "US_CHASE", name: "Chase" },
      { id: "MERCURY", name: "Mercury" },
      { id: "REVOLUT", name: "Revolut" },
      { id: "BREX", name: "Brex" },
      { id: "RAMP", name: "Ramp" },
      { id: "WISE", name: "Wise" },
      { id: "GUSTO", name: "Gusto" },
      { id: "RIPPLING", name: "Rippling" },
      { id: "DEEL", name: "Deel" },
      { id: "BILL_COM", name: "Bill.com" },
      { id: "SPREADSHEETS", name: "Spreadsheets, mostly" },
    ],
  },

  // The account card. One field, because the greeting already took their name:
  // this screen is the address that mints the session, and nothing else.
  {
    key: STEP.EMAIL,
    chapter: "Your account",
    title: "Create your CX Cash account",
    fields: [
      {
        key: "email",
        label: "Work email",
        placeholder: "Your work email",
        inputType: "email",
      },
    ],
  },

  // The collection root. One card per piece of software they named, each opening
  // a screen to connect it, upload for it, or say there is nothing to give. Its
  // answer is a map of software id -> CONNECTED / NO_DATA, and answering it does
  // NOT advance: a lead stays here until they say they're done, which finalizes.
  {
    key: STEP.DATA_HUB,
    chapter: "Your money",
    title:
      "Connect or import the data you have for each of the software below:",
  },

  {
    key: STEP.WAITLIST,
    chapter: "Your spot",
    title: "You're on the list",
    subtitle:
      "Onboarding is concierge, and we work the queue in the order we can rebuild a picture. Here's where you stand.",
  },
];

const BY_KEY = new Map(STEPS.map((step) => [step.key, step]));

export function stepFor(key: string | null | undefined): Step | undefined {
  return key ? BY_KEY.get(key) : undefined;
}

// --- the walk ----------------------------------------------------------------

export type Answers = readonly SavedAnswer[];

export type SavedAnswer = {
  key: string;
  values: readonly string[];
  fields: readonly { key: string; value: string }[];
};

export function valuesOf(answers: Answers, key: string): readonly string[] {
  return answers.find((a) => a.key === key)?.values ?? [];
}

export function fieldsOf(
  answers: Answers,
  key: string
): Record<string, string> {
  const found = answers.find((a) => a.key === key)?.fields ?? [];
  return Object.fromEntries(found.map((f) => [f.key, f.value]));
}

export function answered(answers: Answers, key: string): boolean {
  return answers.some((a) => a.key === key);
}

// Where answering `from` lands. `null` means the walk is over — the hub is the
// last question, and finalizing is what leaves it.
export function nextStep(
  from: string,
  values: readonly string[],
  answers: Answers
): string | null {
  switch (from) {
    case STEP.NAME:
      return STEP.VISITOR_TYPE;
    case STEP.VISITOR_TYPE:
      // No card picked is "Neither": ask them, in their own words, instead.
      if (values.length === 0) return STEP.ROLE_OTHER;
      return values.includes(ADVISOR) ? STEP.CLIENT_TYPES : STEP.COMPANY_TYPE;
    case STEP.ROLE_OTHER:
      // Someone who wrote their own role is describing their own company, so
      // they get the singular question the same way card one does.
      return STEP.COMPANY_TYPE;
    case STEP.COMPANY_TYPE:
      // ...and they have already told us their role, in better words than five
      // cards could offer. Asking again would be the funnel not listening.
      return answered(answers, STEP.ROLE_OTHER) ? STEP.FOCUS : STEP.ROLE;
    case STEP.ROLE:
    case STEP.CLIENT_TYPES:
      return STEP.FOCUS;
    case STEP.FOCUS:
      return STEP.STACK;
    case STEP.STACK:
      return STEP.EMAIL;
    case STEP.EMAIL:
      return STEP.DATA_HUB;
    default:
      return null;
  }
}

// Which set of cards the focus question shows: the company kind they named, if
// they named exactly one we have a set for. The company question allows one
// answer, so a lead lands on their own set; an advisor who works with several
// kinds is not any one of them, and gets the general set.
export function focusGroup(answers: Answers): string {
  const picked = valuesOf(answers, STEP.COMPANY_TYPE).length
    ? valuesOf(answers, STEP.COMPANY_TYPE)
    : valuesOf(answers, STEP.CLIENT_TYPES);

  const only = picked.length === 1 ? picked[0] : null;
  return only && FOCUS_GROUPS.includes(only) ? only : GENERAL;
}

export function optionsFor(step: Step, answers: Answers): Option[] {
  if (step.optionGroups) return step.optionGroups[focusGroup(answers)] ?? [];
  return step.options ?? [];
}

// --- talking to a person -----------------------------------------------------

const NAME_TOKEN = /,?\s*\{name\}/g;

export function firstName(answers: Answers): string {
  return (fieldsOf(answers, STEP.NAME).first_name ?? "").trim();
}

// The step as the lead should read it. Every renderer takes its copy off the
// step, so substituting here reaches all of them and none of them has to know.
export function personalise(step: Step, answers: Answers): Step {
  if (!step.title.includes("{name}") && !step.subtitle?.includes("{name}"))
    return step;

  const name = firstName(answers);
  const say = (text: string) =>
    text.replace(NAME_TOKEN, name ? `, ${name}` : "");

  return {
    ...step,
    title: say(step.title),
    subtitle: step.subtitle ? say(step.subtitle) : undefined,
  };
}

// --- the collection hub ------------------------------------------------------

export const CONNECTED = "CONNECTED";
export const NO_DATA = "NO_DATA";

// What the lead has said about each piece of software they named.
export function hubResolutions(answers: Answers): Record<string, string> {
  return fieldsOf(answers, STEP.DATA_HUB);
}

// The software they said they run on, in the order the stack question offers.
export function stackPicks(answers: Answers): readonly string[] {
  return valuesOf(answers, STEP.STACK);
}
